import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { Button } from '@cfa/react-components';

import { useInvalidateCertificateQueries } from '@/hooks/useInvalidateCertificateQueries';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import { invalidateAllPlanQueries } from '@/utils/queryInvalidation';
import { isApiError } from '@/utils/request';
import { generateTotalTime } from '@/utils/time';

import GenericError from '@/components/Error/GenericError';
import PlanItemBrightcoveVideo from '@/components/PlanCards/PlanItemBrightcoveVideo';
import PlanItemCard from '@/components/PlanCards/PlanItemCard';
import PlanItemProcedure from '@/components/PlanCards/PlanItemProcedure';
import PlanItemQuiz from '@/components/PlanCards/PlanItemQuiz';
import PlanItemTask from '@/components/PlanCards/PlanItemTask';

import Constants, { routes } from '@/constants';

import {
  getPlanAndAssignmentsOptions,
  PlanStep,
  trainTogetherMarkPlanAsComplete,
  trainTogetherUpdateStepStatus,
  UserPlans,
} from '@/_generated/api-clients/xp';
import { trainingModeAtom } from '@/atoms/trainingMode';
import Header from '@/routes/Layout/Header/Header';
import TrainingModeExitButton from '@/routes/Layout/Header/RightHeaderContent/TrainingModeExitButton';
import useTrainingHeartbeatPlanTimer from '@/routes/TrainingPlans/hooks/useTrainingHeartbeatTimer';

interface SectionsAndStepsProps {
  showPlanCompleteModal: () => void;
}

const trainingModePickAtom = focusAtom(trainingModeAtom, o =>
  o.pick(['trainingModeData', 'isBeforeNavigate']),
);

const SectionsAndSteps: React.FC<SectionsAndStepsProps> = ({
  showPlanCompleteModal,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const invalidateCertificates = useInvalidateCertificateQueries();

  const s = useAtomValue(trainingModePickAtom);

  const {
    assignedTeamMembersStatus,
    estimatedMinutes,
    languageSelected,
    planId,
    planLabel,
    planName,
    selectedTeamMembers,
  } = s.trainingModeData;

  const assignedUserIds = useMemo(
    () =>
      selectedTeamMembers
        .map(member => member.adId)
        .filter((id): id is string => !!id),
    [selectedTeamMembers],
  );

  const [activeTask, setActiveTask] = useState('');
  const [activeQuizStepId, setActiveQuizStepId] = useState('');
  const [assignedPlan, setAssignedPlan] = useState<UserPlans>();
  const [headerLabel, setHeaderLabel] = useState('');
  const [header, setHeader] = useState('');
  const [headerSubtext, setHeaderSubtext] = useState('');

  const isPlanComplete = !assignedTeamMembersStatus
    ? false
    : assignedTeamMembersStatus.every(
        teamMember =>
          teamMember.status === Constants.TRAINING_PLANS.COMPLETED ||
          // This OR condition is in case a team member has already registered a Completion of this plan
          // and the lead uses Select All when doing Train Together to force a session. When a team mate
          // registers a Completion, they are UNASSIGNED "permanently" from that plan as it exists in its
          // current state. Therefore we have to check Unassigned + steps completed.
          (teamMember.steps?.length === teamMember.stepsComplete &&
            teamMember.status === Constants.TRAINING_PLANS.UNASSIGNED),
      );

  const {
    data: planAndAssignments,
    error: errorPlan,
    refetch: _refetchAssignedStatus,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId } }),
    enabled: !!planId && isValidPlanId(planId),
  });

  const refetchAssignedStatus = () => {
    if (!isValidPlanId(planId)) {
      throw new Error('Invalid planId');
    }
    return _refetchAssignedStatus();
  };

  const plan = planAndAssignments?.plan;
  const planAssignments = planAndAssignments?.assignments ?? [];

  useEffect(() => {
    if (s.isBeforeNavigate && !planName) {
      return;
    }
    // We route the user back to the Team Members page if they did not access this page through the Training Mode flow
    if (!planName) {
      navigate(routes.plans.managedPlans);
    } else if (planName) {
      setHeader(planName);
      setHeaderLabel(planLabel);

      setHeaderSubtext(
        t('TrainingPlans.trainTogether.estimatedCompletionTime', {
          estimatedTime: generateTotalTime(
            estimatedMinutes,
            t('Generic.hour'),
            t('Generic.mins'),
          ),
        }),
      );
    }
  }, [navigate, t, planName, planLabel, estimatedMinutes, s.isBeforeNavigate]);

  useEffect(() => {
    if (planAndAssignments) {
      setAssignedPlan(planAndAssignments);
    }
  }, [planAndAssignments]);

  useEffect(() => {
    if (assignedPlan?.plan?.name) {
      setHeader(itemByLanguage(assignedPlan.plan.name, '', languageSelected));
    }
  }, [assignedPlan, languageSelected]);

  const isStepCompleted = (stepId: string) =>
    assignedPlan?.assignments
      ?.filter(assignment =>
        assignedUserIds.find(id => assignment.userId === id),
      )
      .every(
        planAssigned =>
          planAssigned.steps?.find(step => step.stepId === stepId)?.status ===
          Constants.TRAINING_PLANS.COMPLETE,
      );

  const getStepStatus = (status: boolean) =>
    status
      ? Constants.TRAINING_PLANS.COMPLETE
      : Constants.TRAINING_PLANS.INCOMPLETE;

  const isStepCompleteById = (stepId?: string) =>
    stepId ? (isStepCompleted(stepId) ?? false) : false;

  const getStepStatusById = (stepId?: string) =>
    getStepStatus(isStepCompleteById(stepId));

  const toggleStepById = (stepId?: string) => {
    if (stepId) {
      onOvalIconToggle(stepId);
    }
  };

  const [startTaskTimer, stopTaskTimer] = useTrainingHeartbeatPlanTimer(
    assignedPlan?.plan?.id ?? '',
    activeTask,
    assignedUserIds,
    getStepStatus(isStepCompleted(activeTask) ?? false),
  );

  const [startQuizTimer, stopQuizTimer] = useTrainingHeartbeatPlanTimer(
    planId,
    activeQuizStepId,
    assignedUserIds,
    getStepStatus(isStepCompleted(activeQuizStepId) ?? false),
  );

  const handleSetActiveTask = useCallback((step: PlanStep) => {
    const stepId = step.id;
    setActiveTask(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  const handleSetActiveQuiz = useCallback((step: PlanStep) => {
    const stepId = step.id;
    setActiveQuizStepId(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  const handleSetActiveVideo = useCallback((step: PlanStep) => {
    const stepId = step.id;
    setActiveTask(prevId => (prevId === stepId ? '' : stepId));
  }, []);

  useEffect(() => {
    if (activeQuizStepId !== '' && isStepCompleted(activeQuizStepId)) {
      setActiveQuizStepId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuizStepId, assignedPlan]);

  // timer for tasks
  useEffect(() => {
    if (activeTask !== '') {
      startTaskTimer();
    } else {
      stopTaskTimer();
    }
    return () => {
      stopTaskTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask]);

  // quiz timer
  useEffect(() => {
    if (activeQuizStepId !== '') {
      startQuizTimer();
    } else {
      stopQuizTimer();
    }
    return () => {
      stopQuizTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuizStepId]);

  if (isApiError(errorPlan)) {
    datadogRum.addError(
      errorPlan instanceof Error ? errorPlan : new Error(String(errorPlan)),
    );
    return <GenericError />;
  }

  const onCardClick = (step: PlanStep, id?: string) => {
    navigate(
      {
        pathname: routes.plans.planDocument({
          planId,
          docId: step.resource?.id ?? '',
        }),
      },
      {
        state: {
          planId: planId,
          planName,
          step,
          id,
          isCompleted: isStepCompleteById(step.id),
          userIds: assignedUserIds,
          stepStatus: getStepStatusById(step.id),
        },
      },
    );
  };

  const onOvalIconToggle = (id: string) => {
    trainTogetherUpdateStepStatus({
      path: { planId, stepId: id },
      body: {
        status: isStepCompleted(id) ? 'INCOMPLETE' : 'COMPLETE',
        userIds: assignedUserIds,
      },
    })
      .then(() => {
        refetchAssignedStatus();
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onCompleteButtonClick = () => {
    trainTogetherMarkPlanAsComplete({
      path: { planId },
      body: { userIds: assignedUserIds },
    })
      .then(async () => {
        await Promise.all([
          invalidateCertificates({ planId }),
          invalidateAllPlanQueries(planId),
        ]);
        showPlanCompleteModal();
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  // Due to legacy decisions, the only reliable use of the 'available' property on a resource is with type === "document".
  // Therefore, we include all resources in sections and checklists, but exclude documents where 'available' === false.
  const areAllStepsComplete = () => {
    const availableResourceCount = plan?.content?.sections
      ? plan.content.sections
          .flatMap(section =>
            section.steps?.filter(
              step =>
                (step.available &&
                  step.type === Constants.STEP_TYPES.DOCUMENT) ||
                step.type !== Constants.STEP_TYPES.DOCUMENT,
            ),
          )
          .concat(
            plan.content.steps?.filter(
              step =>
                (step.available &&
                  step.type === Constants.STEP_TYPES.DOCUMENT) ||
                step.type !== Constants.STEP_TYPES.DOCUMENT,
            ),
          ).length
      : 0;

    return (
      assignedPlan?.plan?.totalSteps !== 0 &&
      assignedPlan?.assignments
        ?.filter(assignedUser =>
          assignedUserIds.find(id => assignedUser.userId === id),
        )
        .map(assignment => assignment.stepsComplete)
        .every(step => step === availableResourceCount)
    );
  };

  const renderCompleteButton = () => (
    <div className="mb-8 flex justify-center">
      <Button
        color="secondary"
        data-testid="CompleteButton"
        disabled={!areAllStepsComplete() || isPlanComplete || false}
        onClick={() => onCompleteButtonClick()}
        variant={isPlanComplete ? 'text' : 'filled'}
      >
        {isPlanComplete ? t('Button.completed') : t('Button.complete')}
      </Button>
    </div>
  );

  return (
    <>
      <Header
        headerLabel={headerLabel}
        headerName={header}
        headerSubtext={headerSubtext}
        rightContent={<TrainingModeExitButton />}
      />
      <div className="mt mx-auto w-full lg:w-1/2">
        {assignedPlan?.plan?.content?.steps?.map((step, index) => {
          switch (step.type) {
            case Constants.STEP_TYPES.QUIZ:
              return (
                <PlanItemCard key={index}>
                  <PlanItemQuiz
                    isActive={activeQuizStepId === step.id}
                    isComplete={isStepCompleteById(step.id)}
                    language={languageSelected}
                    onClick={handleSetActiveQuiz}
                    onRefetch={refetchAssignedStatus}
                    planId={planId}
                    statuses={assignedPlan.assignments ?? []}
                    step={step}
                    users={assignedUserIds}
                  />
                </PlanItemCard>
              );
            case Constants.STEP_TYPES.TASK:
              return (
                <PlanItemCard key={index}>
                  <PlanItemTask
                    isActive={activeTask === step.id}
                    isComplete={isStepCompleteById(step.id)}
                    language={languageSelected}
                    onClick={handleSetActiveTask}
                    onCompleteToggle={() => toggleStepById(step.id)}
                    planId={planId}
                    statuses={planAssignments}
                    step={step}
                  />
                </PlanItemCard>
              );
            case Constants.STEP_TYPES.DOCUMENT: {
              const stepResourceType = step.resource?.type?.toLowerCase();
              return stepResourceType ===
                Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                <PlanItemCard key={index}>
                  <PlanItemBrightcoveVideo
                    isActive={activeTask === step.id}
                    isComplete={isStepCompleteById(step.id)}
                    onClick={handleSetActiveVideo}
                    onCompleteToggle={() => toggleStepById(step.id)}
                    statuses={planAssignments}
                    step={step}
                    users={[]}
                  />
                </PlanItemCard>
              ) : (
                <PlanItemCard key={index}>
                  <PlanItemProcedure
                    isActive={activeTask === step.id}
                    isComplete={isStepCompleteById(step.id)}
                    isMissing={!step.available}
                    language={languageSelected}
                    onClick={() => onCardClick(step)}
                    onCompleteToggle={() => toggleStepById(step.id)}
                    statuses={planAssignments}
                    step={step}
                  />
                </PlanItemCard>
              );
            }
            default:
              return null;
          }
        })}
        {assignedPlan?.plan?.content?.sections?.map(
          section =>
            !!section.steps &&
            !!section.steps.length && (
              <PlanItemCard
                key={section.id}
                sectionName={itemByLanguage(section.name, '', languageSelected)}
              >
                {section.steps.map((step, stepIndex) => {
                  switch (step.type) {
                    case Constants.STEP_TYPES.QUIZ:
                      return (
                        <PlanItemQuiz
                          isActive={activeQuizStepId === step.id}
                          isComplete={isStepCompleteById(step.id)}
                          key={stepIndex}
                          language={languageSelected}
                          onClick={handleSetActiveQuiz}
                          onRefetch={refetchAssignedStatus}
                          planId={planId}
                          statuses={assignedPlan.assignments ?? []}
                          step={step}
                          users={assignedUserIds}
                        />
                      );
                    case Constants.STEP_TYPES.TASK:
                      return (
                        <PlanItemTask
                          isActive={activeTask === step.id}
                          isComplete={isStepCompleteById(step.id)}
                          key={stepIndex}
                          language={languageSelected}
                          onClick={handleSetActiveTask}
                          onCompleteToggle={() => toggleStepById(step.id)}
                          planId={planId}
                          statuses={planAssignments}
                          step={step}
                        />
                      );
                    case Constants.STEP_TYPES.DOCUMENT: {
                      const stepResourceType =
                        step.resource?.type?.toLowerCase();
                      return stepResourceType ===
                        Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                        <PlanItemBrightcoveVideo
                          isActive={activeTask === step.id}
                          isComplete={isStepCompleteById(step.id)}
                          key={stepIndex}
                          onClick={handleSetActiveVideo}
                          onCompleteToggle={() => toggleStepById(step.id)}
                          statuses={planAssignments}
                          step={step}
                          users={[]}
                        />
                      ) : (
                        <PlanItemProcedure
                          isActive={activeTask === step.id}
                          isComplete={isStepCompleteById(step.id)}
                          isMissing={!step.available}
                          key={stepIndex}
                          language={languageSelected}
                          onClick={() => onCardClick(step)}
                          onCompleteToggle={() => toggleStepById(step.id)}
                          statuses={planAssignments}
                          step={step}
                        />
                      );
                    }
                    default:
                      return null;
                  }
                })}
              </PlanItemCard>
            ),
        )}
        {assignedPlan && renderCompleteButton()}
      </div>
    </>
  );
};

export default SectionsAndSteps;
